import { useId, type ReactNode } from "react";
import { Button, Heading, Tooltip } from "@knowtex-ai/components";
import { HelpCircle, SidebarCollapse } from "iconoir-react";

interface SmartAssistHeaderProps {
  /** Screen-reader text for the help control; also the tooltip unless helpContent is set. */
  helpText: string;
  helpContent?: ReactNode;
  description?: ReactNode;
  descriptionId?: string;
  onMinimize?: () => void;
  minimizeControlsId?: string;
  historyError: string;
  canClearHistory: boolean;
  onClearHistory: () => void;
}

export default function SmartAssistHeader({
  helpText,
  helpContent,
  description,
  descriptionId,
  onMinimize,
  minimizeControlsId,
  historyError,
  canClearHistory,
  onClearHistory,
}: SmartAssistHeaderProps) {
  const helpTooltipId = useId();

  return (
    <>
      <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-100 px-4 py-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Heading as="h3" size="small">
              Smart Assist
            </Heading>
            <Tooltip side="bottom" delay={0} content={helpContent ?? helpText}>
              <button
                type="button"
                className="text-light-grey transition-colors hover:text-accent1 focus:text-accent1"
                aria-label="Smart Assist request examples and limitations"
                aria-describedby={helpTooltipId}
              >
                <HelpCircle className="h-4 w-4" aria-hidden="true" />
              </button>
            </Tooltip>
            <span id={helpTooltipId} role="tooltip" className="sr-only">
              {helpText}
            </span>
          </div>
          {description && (
            <p id={descriptionId} className="mb-0 mt-1 break-words text-sm text-light-grey">
              {description}
            </p>
          )}
        </div>
        {onMinimize && (
          <div className="flex shrink-0 items-center">
            <Button
              type="button"
              variant="secondary"
              size="small"
              onClick={onMinimize}
              icon={SidebarCollapse}
              aria-label="Minimize Smart Assist"
              aria-controls={minimizeControlsId}
              aria-expanded="true"
              title="Minimize Smart Assist"
            />
          </div>
        )}
      </div>

      <div
        className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50/70 px-4 py-2"
        aria-label="Smart Assist conversation controls"
      >
        <span className="text-xs font-semibold uppercase tracking-wide text-light-grey">
          Conversation
        </span>
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-3">
          {historyError && (
            <span role="alert" className="text-right text-xs font-semibold text-red-600">
              {historyError}
            </span>
          )}
          <Button
            type="button"
            variant="text"
            size="small"
            onClick={onClearHistory}
            aria-label="Clear History"
            disabled={!canClearHistory}
          >
            Clear chat
          </Button>
        </div>
      </div>
    </>
  );
}
